import { ethers } from 'ethers';

/**
 * Transaction status values
 */
export const TX_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
  REJECTED: 'rejected'
};

/**
 * Generate a unique transaction ID
 * @returns {string} - Unique transaction ID
 */
const generateTxId = () => {
  return `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

class TransactionService {
  constructor() {
    this.transactions = new Map();
    this.listeners = new Set();
  }

  /**
   * Add a listener for transaction updates
   * @param {Function} listener - Callback function to be called on transaction updates
   * @returns {Function} - Function to remove the listener
   */
  addListener(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners about a transaction update
   * @param {Object} transaction - Transaction object 
   */
  notifyListeners(transaction) {
    this.listeners.forEach(listener => {
      try {
        listener(transaction);
      } catch (error) {
        console.error('Error in transaction listener:', error);
      }
    });
  }

  /**
   * Send a transaction
   * @param {Promise} transactionPromise - Promise that resolves to a transaction
   * @param {Object} options - Transaction options
   * @param {string} options.title - Transaction title
   * @param {string} options.description - Transaction description
   * @returns {Object} - Transaction object
   */
  async sendTransaction(transactionPromise, options = {}) {
    const id = generateTxId();
    const timestamp = Date.now();
    const transactionType = options.type || 'transaction';
    
    // Create initial transaction object
    const transaction = {
      id,
      hash: null,
      status: TX_STATUS.PENDING,
      title: options.title || 'Transaction',
      description: options.description || 'Transaction in progress',
      type: transactionType,
      timestamp,
      confirmations: 0,
      receipt: null,
      error: null,
    };
    
    // Add to transactions map
    this.transactions.set(id, transaction);
    
    // Notify listeners
    this.notifyListeners(transaction);
    
    try {
      // Wait for transaction to be submitted
      const tx = await transactionPromise;
      
      // Update transaction with hash
      transaction.hash = tx.hash;
      this.transactions.set(id, transaction);
      this.notifyListeners(transaction);
      
      // Wait for confirmation
      const receipt = await tx.wait();
      
      // Update transaction with receipt
      transaction.status = receipt.status ? TX_STATUS.CONFIRMED : TX_STATUS.FAILED;
      transaction.receipt = receipt;
      transaction.confirmations = 1;
      
      this.transactions.set(id, transaction);
      this.notifyListeners(transaction);
      
      return {
        ...transaction,
        transaction: tx,
      };
    } catch (error) {
      // Check if user rejected transaction
      const isRejected = error.code === 4001 || 
                        (error.message && error.message.includes('user rejected transaction'));
      
      // Update transaction with error
      transaction.status = isRejected ? TX_STATUS.REJECTED : TX_STATUS.FAILED;
      transaction.error = error;
      
      this.transactions.set(id, transaction);
      this.notifyListeners(transaction);
      
      // Re-throw the error for the caller to handle
      throw error;
    }
  }

  /**
   * Get a transaction by ID
   * @param {string} id - Transaction ID 
   * @returns {Object} - Transaction object
   */
  getTransaction(id) {
    return this.transactions.get(id) || null;
  }

  /**
   * Get all transactions
   * @returns {Array} - Array of transaction objects
   */
  getAllTransactions() {
    return Array.from(this.transactions.values()).sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Get pending transactions
   * @returns {Array} - Array of pending transaction objects
   */
  getPendingTransactions() {
    return this.getAllTransactions().filter(tx => tx.status === TX_STATUS.PENDING);
  }

  /**
   * Clear all transactions
   */
  clearTransactions() {
    this.transactions.clear();
    this.notifyListeners(null);
  }

  /**
   * Estimate gas for a transaction
   * @param {Function} txFunction - Function that returns a transaction object
   * @param {Array} args - Arguments to pass to the transaction function
   * @param {Object} options - Gas estimation options
   * @returns {Object} - Gas limit and estimated gas
   */
  async estimateGas(txFunction, args = [], options = {}) {
    try {
      const gasEstimate = await txFunction(...args).estimateGas(options);
      // Add 20% buffer to gas estimate
      const gasLimit = gasEstimate.mul(ethers.BigNumber.from(120)).div(ethers.BigNumber.from(100));
      
      return {
        gasEstimate,
        gasLimit
      };
    } catch (error) {
      console.error('Gas estimation error:', error);
      throw error;
    }
  }

  /**
   * Format transaction error for display
   * @param {Error} error - Transaction error
   * @returns {string} - Formatted error message
   */
  formatError(error) {
    if (!error) return 'Unknown error';
    
    // User rejected transaction
    if (error.code === 4001) {
      return 'Transaction was rejected';
    }
    
    // Check for specific error messages
    const errorMessage = error.message || error.toString();
    
    // Gas related errors
    if (errorMessage.includes('insufficient funds')) {
      return 'Insufficient funds for transaction';
    }
    
    if (errorMessage.includes('gas required exceeds allowance')) {
      return 'Transaction exceeds gas limit';
    }
    
    // Contract reverted
    if (errorMessage.includes('reverted')) {
      // Try to extract reason
      const match = errorMessage.match(/reverted with reason string ['"](.+)['"]/);
      if (match && match[1]) {
        return `Transaction reverted: ${match[1]}`;
      }
      return 'Transaction reverted';
    }
    
    // Network error
    if (errorMessage.includes('network error') || errorMessage.includes('timeout')) {
      return 'Network error, please try again';
    }
    
    // Internal JSON-RPC error
    if (errorMessage.includes('Internal JSON-RPC error')) {
      try {
        const jsonMatch = errorMessage.match(/{.*}/);
        if (jsonMatch) {
          const jsonError = JSON.parse(jsonMatch[0]);
          if (jsonError.message) {
            return jsonError.message;
          }
        }
      } catch (e) {
        // Parsing error, fallback to default
      }
    }
    
    // Default to original message
    return errorMessage;
  }
}

// Create a singleton instance
const transactionService = new TransactionService();

export default transactionService; 